import type { ComponentPropsWithoutRef } from "react";
import { Link } from "react-router-dom";
import { cn } from "../../lib/cn";

// 共有プリミティブ。g-02/g-03 のダークピル CTA を基準に variant 化（guide-app と同一設計）。
// タッチターゲットを 44px 以上に保つ（h-11/h-12）。
type Variant = "solid" | "outline" | "ghost" | "danger";
type Size = "md" | "lg" | "block";

const base =
  "inline-flex items-center justify-center gap-2 rounded-pill font-semibold transition " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 " +
  "disabled:cursor-not-allowed disabled:opacity-50";

const variants: Record<Variant, string> = {
  solid: "bg-ink text-white hover:bg-ink-soft",
  outline: "border border-line bg-paper text-ink hover:bg-canvas",
  ghost: "text-ink hover:bg-canvas",
  danger: "bg-danger text-white hover:opacity-90",
};

const sizes: Record<Size, string> = {
  md: "h-11 px-5 text-sm",
  lg: "h-12 px-7 text-[15px]",
  block: "h-12 w-full px-7 text-[15px]",
};

type CommonProps = { variant?: Variant; size?: Size; className?: string };

export function Button({
  variant = "solid",
  size = "md",
  className,
  ...props
}: CommonProps & ComponentPropsWithoutRef<"button">) {
  return <button className={cn(base, variants[variant], sizes[size], className)} {...props} />;
}

/** 外部 URL（別配信の guide-app など）へ飛ぶ anchor。react-router の Link は同一 SPA 内専用なので分ける。 */
export function ButtonAnchor({
  variant = "solid",
  size = "md",
  className,
  href,
  children,
}: CommonProps & { href: string; children: React.ReactNode }) {
  return (
    <a href={href} className={cn(base, variants[variant], sizes[size], className)}>
      {children}
    </a>
  );
}

export function ButtonLink({
  variant = "solid",
  size = "md",
  className,
  to,
  children,
}: CommonProps & { to: string; children: React.ReactNode }) {
  return (
    <Link to={to} className={cn(base, variants[variant], sizes[size], className)}>
      {children}
    </Link>
  );
}
